import { encode } from '@src/shared/utils/qs'
import { selectUnverifiedPhone } from '@store/user/reducer'
import {
  selectApplicantId,
  selectApplicantEmail,
  selectApplicantForgotKey,
  selectPrevApply
} from '@store/applicant/reducer'
import { local } from '@src/shared/utils/storage'
import { getForterToken } from '@src/shared/utils/forter'
import { FORTER_HEADER_NAME } from '@src/shared/constants'
import { gtm } from '@src/shared/utils/analytics'
import { INDEX_URL } from '@helpers/urls'
import {
  APPLICANT_TOKEN,
  CUSTOMER_CONFIRMED_SUCCESS,
  CLEAR_APPLICANT_ERROR,
  CUSTOMER_PASSWORD_CREATED,
  CUSTOMER_APPLY_SUCCESS,
  CUSTOMER_APPLY_REQUEST,
  GET_MARKETS_REQUEST,
  GET_MARKETS_SUCCESS,
  CLEAR_MARKETS,
  GET_MARKETS_ERROR,
  HYDRATE_APPLICANT,
  APPLICANT_RESPONSE_ERROR,
  SEND_VERIFICATION_ATTEMPT,
  SEND_VERIFICATION_SUCCESS,
  CLEAR_PHONE_VERIFICATION,
  LOAD_APPS_REQUEST,
  LOAD_APPS_SUCCESS
} from '@store/constants'
import { handleError, stateRequest, values } from '@store/utils'
import request from '@store/request'
import { signIn, fetchCustomer, handleCustomerError } from '@store/user/actions'

// Needed for fixing user account details when account already exists with phone associated to different email (see BREAD-2071)
export const resubmitApply = email => async (dispatch, getState) => {
  // first get the params from the signup form and update the email
  const params = selectPrevApply(getState())
  if (!params) return

  params.email = email
  try {
    // submit the signup form again
    const data = await postApply(params)
    dispatch({
      type: CUSTOMER_APPLY_SUCCESS,
      params: null,
      data
    })
  } catch (err) {
    dispatch(handleApplicantError(err))
  }
}

export const confirm = target => async (dispatch, getState) => {
  const { token, coreApi, accountId } = stateRequest(getState())

  try {
    const data = await postApply(values(target))

    dispatch({
      type: CUSTOMER_APPLY_SUCCESS,
      data
    })

    const url = `${coreApi}/api/v6/customers/${accountId}`
    // @ts-expect-error -- TODO fix
    const { first_name: forename, last_name: surname } = values(target)
    const customer = await request(token)(url, 'post', encode({ forename, surname }))

    dispatch({
      type: CUSTOMER_CONFIRMED_SUCCESS,
      data: customer.data.customer
    })

    gtm({
      event: 'Confirm_Information'
    })
  } catch (err) {
    dispatch(handleApplicantError(err))
  }
}

const postApply = async params =>
  request()('/submit', 'post', encode(Object.assign({ _csrf: window.__FVR__.csrf }, params)), {
    [FORTER_HEADER_NAME]: getForterToken('submit')
  }).then(({ data }) => {
    // only set token on a successful post
    local.set(APPLICANT_TOKEN, data)
    return data
  })

export const apply = target => async dispatch => {
  const params = values(target)
  try {
    dispatch({
      type: CUSTOMER_APPLY_REQUEST
    })
    const data = await postApply(params)
    dispatch({
      type: CUSTOMER_APPLY_SUCCESS,
      params,
      data
    })
  } catch (err) {
    dispatch(handleApplicantError(err))
  }
}

export const createPassword = target => async (dispatch, getState) => {
  const {
    sourceRequest: { coreApi }
  } = getState()
  const email = selectApplicantEmail(getState())
  const url = `${coreApi}/api/v6/forgotpassword`
  // @ts-expect-error -- TODO fix
  const { confirmation, password } = values(target)
  const forgotKey = selectApplicantForgotKey(getState())

  try {
    const data = await request()(
      url,
      'post',
      encode({
        confirmation,
        password,
        forgot_key: forgotKey
      })
    )

    dispatch({
      type: CUSTOMER_PASSWORD_CREATED,
      data
    })
  } catch (err) {
    dispatch(handleApplicantError(err))
    return
  }

  dispatch(signIn(email, password))
}

export const fetchApps = () => async (dispatch, getState) => {
  const { token, coreApi } = stateRequest(getState())
  const url = `${coreApi}/api/v6/app_versions/runner`

  try {
    dispatch({
      type: LOAD_APPS_REQUEST
    })

    const apps = await request(token)(url)

    dispatch({
      type: LOAD_APPS_SUCCESS,
      data: apps.data
    })
  } catch (err) {
    dispatch(handleCustomerError(err))
  }
}

export const fetchApplicant = () => async (dispatch, getState) => {
  try {
    const { accountId } = stateRequest(getState())
    const { data } = (await request()(
      '/applicant',
      'post',
      encode({
        _csrf: window.__FVR__.csrf,
        accountId
      })
    )) || { _csrf: window.__FVR__.csrf }
    local.set(APPLICANT_TOKEN, data)

    dispatch({
      type: HYDRATE_APPLICANT,
      data
    })
  } catch (err) {
    dispatch(handleApplicantError(err))
  }
}

export const verifyPhone = code => async (dispatch, getState) => {
  const state = getState()

  try {
    const { token, coreApi, accountId } = stateRequest(state)
    const url = `${coreApi}/api/v6/customers/${accountId}/verifications/${code}`
    await request(token)(url, 'post')
  } catch (err) {
    dispatch(handleCustomerError(err))
    return
  }

  dispatch(fetchCustomer())

  const phone = selectUnverifiedPhone(state)
  const id = selectApplicantId(state)

  if (!id) {
    return
  }

  try {
    await request()(
      '/applicant-update',
      'post',
      encode({
        _csrf: window.__FVR__.csrf,
        telephone: phone,
        id
      })
    )
  } catch (err) {
    dispatch(handleApplicantError(err))
  }

  dispatch(fetchApplicant())
}

export const sendVerificationCode = () => async (dispatch, getState) => {
  dispatch({ type: SEND_VERIFICATION_ATTEMPT })

  const { token, coreApi, accountId } = stateRequest(getState())
  const url = `${coreApi}/api/v6/customers/${accountId}/verifications`

  try {
    const data = await request(token)(url, 'post')
    dispatch({
      type: SEND_VERIFICATION_SUCCESS,
      data
    })

    setTimeout(() => {
      dispatch({ type: CLEAR_PHONE_VERIFICATION })
    }, 5000)
  } catch (err) {
    dispatch(handleCustomerError(err))
  }
}

export const clearApplicantError = () => ({
  type: CLEAR_APPLICANT_ERROR
})

export const hydrateApplicant = () => dispatch => {
  const applicant = local.get('applicant')

  if (applicant) {
    const { pathname: path } = window.location
    if (path !== INDEX_URL) {
      return dispatch({
        type: HYDRATE_APPLICANT,
        data: applicant
      })
    }
    local.set('applicant')
  }
}

export const getZipMarkets =
  (zipcode, radius = 25) =>
  async dispatch => {
    dispatch({ type: GET_MARKETS_REQUEST })
    const url = `/applicant/markets?zipcode=${zipcode}${radius ? `&radius=${radius}` : ''}`
    try {
      const { data } = await request()(url, 'get')
      dispatch({
        type: GET_MARKETS_SUCCESS,
        data
      })
    } catch (err) {
      if (err.response?.status === 400) {
        dispatch({
          type: GET_MARKETS_SUCCESS,
          data: []
        })
      }
      dispatch({
        type: GET_MARKETS_ERROR,
        error: handleError(err)
      })
    }
  }

export const clearMarkets = () => async dispatch => {
  dispatch({ type: CLEAR_MARKETS })
}

export const handleApplicantError = error => dispatch => {
  dispatch({
    type: APPLICANT_RESPONSE_ERROR,
    error: handleError(error)
  })
}
